// src/pages/Work/UseCases/DevelopmentTools/DevelopmentTools.js
import React from 'react';
import './DevelopmentTools.css';

function DevelopmentTools({ onNavigate, isFirst, isLast }) {
  return (
    <div className="use-case-container">
      {/* Optional overall heading */}
      <div className="use-case-header">
        <h3 className="use-case-title">
          Development Tools / "Menu configuration" tool
        </h3>
        <div className="navigation-buttons">
          <button
            onClick={() => onNavigate('prev')}
            disabled={isFirst}
            className="nav-button"
          >
            &lt;
          </button>
          <button
            onClick={() => onNavigate('next')}
            disabled={isLast}
            className="nav-button"
          >
            &gt;
          </button>
        </div>
      </div>

      {/* Overview */}
      <div id="devtools-overview" className="use-case-section">
        <h2 className="section-title">Overview</h2>
        <div className="overview-content">
          <p>
            In my role as a UX designer, I <strong>led the redesign of a logistics developer application</strong> to enhance usability and efficiency. Key contributions included:
          </p>
          <ul className="overview-list">
            <li>
              <strong>Streamlining Tools:</strong> Identified and addressed user pain points to optimize developer workflows.
            </li>
            <li>
              <strong>Modern Interface Design:</strong> Developed an intuitive interface supporting flexible, user-centered configurations.
            </li>
            <li>
              <strong>Collaborative Development:</strong> Worked closely with <strong>development teams</strong> and the <strong>Product Owner</strong> to ensure alignment with user needs and business objectives.
            </li>
          </ul>
        </div>
      </div>

      {/* Specification */}
      <div id="devtools-specification" className="use-case-section">
        <h2 className="section-title">Specification</h2>
        <div className="specification-content">
          <h1 className="tool-name">"Menu configuration" tool</h1>
          <div className="spec-details">
            <div className="spec-row">
              <div className="spec-label">Duration:</div>
              <div>1 year</div>
            </div>
            <div className="spec-row">
              <div className="spec-label">Tools Used:</div>
              <div>Figma, Confluence, Jira, Fig Jam</div>
            </div>
          </div>
          <div>
            <h3 style={{ color: '#0074f0', marginBottom: '0' }}>Goal</h3>
            <p className="goal-text">
              Improve a tool within the developer application that enables the implementation team to quickly and effectively configure business menus.
            </p>
          </div>
          {/* Specification Image */}
          <img
            src={require("../../../../assets/use-case-specification.png")}
            alt="Use Case Specification Illustration"
            className="specification-image"
            style={{ 
              width: '100%', 
              maxWidth: '600px', 
              marginTop: '2rem',
              borderRadius: '4px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
            }}
          />
        </div>
      </div>

      {/* The Problem */}
      <div id="devtools-problem" className="use-case-section">
        <div className="problem-header">
          <h2 className="section-title">
            <span className="highlight-blue">The Problem</span> Challenge
          </h2>
        </div>
        <div className="problem-content">
          <p>
            The primary challenge was taking on the role of <strong>Lead UX/UI Designer</strong>, where I was responsible for overseeing the entire UX process and delivering the final UI design.
          </p>
          <p>
            In this project, I conducted a thorough <strong>validation of user needs</strong> and <strong>optimized the existing tool</strong> to support future development. My role included <strong>identifying pain points</strong>, understanding user requirements, and <strong>streamlining</strong> the tool's <strong>organizational structure</strong> to ensure an intuitive and seamless experience. I focused on developing effective, user-centric solutions that addressed key challenges and significantly enhanced the overall user experience.
          </p>
          <div className="impact-section">
            <h3 className="highlight-blue">My impact</h3>
            <p>Here are some of the notes, highlights, and deliverables from this project:</p>
            <ul className="impact-list">
              <li>
                I <strong>leverage data</strong> from the <strong>research</strong> to create user friendly tools for different users.
              </li>
              <li>
                <strong>Transform</strong> existing <strong>tool features</strong> to be more <strong>practical</strong> and valuable solutions
              </li>
              <li>
                <strong>Redesigned</strong> the tool's <strong>interface</strong> to align with established brand <strong>guidelines</strong>, ensuring visual consistency and usability.
              </li>
              <li>
                I participate in internal review sessions and incorporate <strong>actionable feedback</strong>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* The Process */}
      <div id="devtools-process" className="use-case-section">
        <h2 className="section-title">
          <span className="highlight-blue">The Process</span> Approach
        </h2>
        <div className="process-content">
          <p>
            I outlined the key steps of my design process and highlighted the UX principles and knowledge applied throughout.
          </p>

          <h3 className="highlight-blue">Users Depth-Interviews</h3>
          <p>Conducted a couple of in-depth interviews to understand:</p>
          <ul className="process-list">
            <li>
              The <strong>different business contexts</strong> in which this tool is used to configure menus (goals).
            </li>
            <li>
              The <strong>development patterns</strong> involved in the configuration process within this tool (step-by-step process).
            </li>
            <li>
              The main <strong>usability obstacles</strong> users face when using this tool (pain points).
            </li>
            <li>
              Which <strong>features are essential</strong> for users and how to create a clear feature hierarchy.
            </li>
          </ul>

          <h3 className="highlight-blue">Competitive research</h3>
          <ul className="process-list">
            <li>
              Explored <strong>top-rated apps</strong> similar to ours to <strong>compare key features</strong> and solutions for menu configuration.
            </li>
            <li>
              Focused on <strong>identifying</strong> the <strong>best practices</strong> and <strong>solutions</strong>.
            </li>
          </ul>

          <h3 className="highlight-blue">Persona</h3>
          <ul className="process-list">
            <li>
              After organizing the research findings, I developed a persona that accurately <strong>represents</strong> the <strong>primary user group</strong>.
            </li>
          </ul>

          <h3 className="highlight-blue">User stories</h3>
          <ul className="process-list">
            <li>
              The primary use case was inspired by synthesized research from user interviews and competitive analysis, highlighting the challenges of the tool. The <strong>MVP</strong> (Minimum Viable Product) focuses on identifying the <strong>essential features needed</strong> to configure the menu effectively that's why I use user stories.
            </li>
          </ul>

          <h3 className="highlight-blue">User flow</h3>
          <ul className="process-list">
            <li>
              After defining the app's features, I <strong>designed task flows</strong> to outline the <strong>primary user interactions</strong>. Guide the user through a <strong>step-by-step process</strong> to configure a menu with submenu functionality upon launching the app.
            </li>
          </ul>
        </div>
      </div>

      {/* The Solution */}
      <div id="devtools-solution" className="use-case-section">
        <h2 className="section-title">
          <span className="highlight-blue">Solution</span> Ideation
        </h2>
        <div className="solution-content">
          {/* Solution Image */}
          <img
            src={require("../../../../assets/solution-ideation.png")}
            alt="Solution Ideation Illustration"
            className="solution-image"
            style={{ 
              width: '100%', 
              maxWidth: '600px', 
              marginBottom: '1.5rem',
              borderRadius: '4px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
            }}
          />
          
          <p>
            I led the ideation process by directly addressing the <strong>identified pain points</strong>, crafting innovative and <strong>user-friendly solutions</strong>. Each solution was meticulously designed to <span className="highlight-blue">enhance usability</span>, <span className="highlight-blue">streamline workflows</span>, and significantly improve the overall user experience. My approach ensured that <strong>user needs were prioritized</strong>, and the solutions aligned with project goals and business objectives
          </p>

          <h3 className="highlight-blue">Information Architecture and Research Approach</h3>
          <p>
            I focused on <strong>Information Architecture</strong> to ensure content was organized logically and intuitively, helping users easily locate and understand information.
          </p>
          <p>My approach:</p>
          <ul className="solution-list">
            <li><strong>Simplified complexity</strong> through effective categorization and grouping.</li>
            <li>Created clear, logical navigation paths to <strong>reduce</strong> cognitive <strong>load</strong>.</li>
            <li>Enhanced usability by <strong>eliminating</strong> duplicate or <strong>unnecessary content</strong>.</li>
            <li><strong>Streamlined collaboration</strong> and the development process.</li>
          </ul>

          <div className="card-sorting-section">
            <h3 className="highlight-blue">Card Sorting</h3> with <span className="highlight-blue">Optimal Workshop</span>
            
            {/* Card Sorting Image */}
            <img
              src={require("../../../../assets/card-sorting.png")}
              alt="Card Sorting Illustration"
              className="card-sorting-image"
              style={{ 
                width: '100%', 
                maxWidth: '600px', 
                marginBottom: '1.5rem',
                borderRadius: '4px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
              }}
            />
            
            <p>
              Using <strong>Optimal Workshop</strong>, I conducted card sorting to understand how users categorize and organize information. This <strong>revealed unclear, duplicated, or missing elements</strong>, providing key insights to improve navigation and refine the tool's design.
            </p>
            <p>
              This process led to a user-centered structure that <strong>enhanced the tool's functionality</strong> and usability
            </p>
          </div>
        </div>
      </div>

      {/* Design Features */}
      <div id="devtools-designfeatures" className="use-case-section">
        <h2 className="section-title">
          <span className="highlight-blue">Design Features</span> Visualization
        </h2>
        
        <div className="design-features-content">
          <h3 className="highlight-blue">Lo-fi wireframes</h3>
          <img
            src={require("../../../../assets/lofi-wireframes.png")}
            alt="Lo-fi wireframes illustration"
            className="wireframe-image"
            style={{ 
              width: '100%', 
              maxWidth: '600px', 
              marginBottom: '1.5rem',
              borderRadius: '4px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
            }}
          />
          <p>
            I created low-fidelity prototypes to quickly visualize user flows, allowing me to identify navigation and interaction issues early in the process. By focusing on <strong>layout, structure</strong>, and <strong>functionality</strong> rather than visual design, I <strong>prioritized the core</strong> user experience and ensured <strong>the foundation</strong> was <strong>solid</strong> before moving to detailed designs.
          </p>

          <p>
            This approach enabled <strong>rapid iteration</strong>, efficient idea testing 1:1 interview to degt feedback, and early problem-solving to ensure a solid design foundation.
          </p>

          <h3 className="highlight-blue">Hi- fi wireframes</h3>
          <img
            src={require("../../../../assets/hifi-wireframes.png")}
            alt="Hi-fi wireframes illustration"
            className="wireframe-image"
            style={{ 
              width: '100%', 
              maxWidth: '600px', 
              marginBottom: '1.5rem',
              borderRadius: '4px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
            }}
          />
          
          <p>I played a <strong>pivotal role</strong> in creating high-fidelity prototypes that brought the product vision to life.</p>

          <h3 className="highlight-blue">My contributions included:</h3>
          <ul className="contributions-list">
            <li>
              <strong>Developing a Detailed Product Vision:</strong> I crafted a comprehensive vision for the product, ensuring alignment with user needs and business objectives.
            </li>
            <li>
              <strong>Delivering Precise Prototypes:</strong> I produced accurate prototypes that served as blueprints for developers, reducing miscommunication and ensuring accurate implementation.
            </li>
            <li>
              <strong>Providing Clear Visuals and Interactions:</strong> I supplied clear visual designs and interactive elements to help developers align on design decisions and confidently approve the direction.
            </li>
            <li>
              <strong>Encouraging Meaningful Feedback:</strong> I developed prototypes that facilitated valuable feedback, ensuring they met user expectations.
            </li>
          </ul>

          <p>
            Through my work, I ensured the design was both user-centered and aligned with project goals. Additionally, I <strong>updated the color scheme</strong> to <strong>align with the company's branding guidelines</strong>, aiming for a <strong>cohesive</strong> and professional <strong>look</strong> while enhancing usability. I carefully selected colors that adhered to the brand identity, <strong>improved readability, and minimized visual distractions</strong>.
          </p>
        </div>
      </div>

      {/* Conclusion */}
      <div id="devtools-conclusion" className="use-case-section">
        <h2 className="section-title">
          <span className="highlight-blue">Conclusion</span>
        </h2>
        <div className="conclusion-content">
          <p>
            <strong>Leading this UX project</strong> involved configuring menus to meet diverse user needs. Through research and problem-solving, I <strong>identified critical issues</strong>, notably the rigidity of the <strong>initial tool</strong>. Implementing solutions <strong>transformed it into a more efficient system</strong>, enhancing developer satisfaction.
          </p>

          <p>
            <strong>Collaborating with developers</strong> highlighted the importance of a <strong>well-structured system architecture</strong>. This partnership deepened my understanding of how <strong>thoughtful design</strong> leads to <strong>intuitive interfaces</strong>. Aligning UX principles with architectural planning resulted in a system that met user needs and facilitated efficient development.
          </p>

          <p>
            This experience reinforced the value of integrating UX considerations early in the design process. It <strong>taught me to trust the research process</strong>, listen to users, adapt to their needs, and find <strong>creative solutions to complex problems</strong>. Managing multi-level development and gathering information from diverse stakeholders <strong>improved my organizational and leadership skills</strong>.
          </p>

          <p>
            This <strong>journey highlighted the complexities of leading UX design projects</strong> and the necessity of balancing user advocacy with technical constraints. It emphasized the importance of <strong>effective communication</strong> and <strong>collaboration among cross-functional teams</strong> to achieve a harmonious balance between user-centric design and system architecture.
          </p>
        </div>
      </div>
    </div>
  );
}

export default DevelopmentTools;